import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { paymentMethodRequired } from '@lds-efsp/shared-contracts';
import { useEFiling } from '../contexts/EFilingContext';
import { FEE_TOTAL_UNRESOLVED_LABEL, presentFeeTotal } from '../lib/fee-display';

interface OrderDetailsTabProps {
  brand: BrandConfig;
}

const OrderDetailsTab: React.FC<OrderDetailsTabProps> = ({ brand }) => {
  const { wizardState, updateOrderDetails } = useEFiling();
  const { orderDetails, courtFees, documents, parties, attorneys, orderInfo, caseInfo } = wizardState;

  // SPEC-SOR-343 (R7/R12): the bar renders the resolved record's total through
  // the shared presenter — the state label while the quote is not resolved,
  // never a figure a draft's legacy accumulator might still carry. The payment
  // rule below is the shared decider keyed on the record's total, so this
  // checklist can no longer disagree with the whole-order validator about who
  // owes a card.
  const feeTotal = presentFeeTotal(courtFees);
  const paymentRequired = paymentMethodRequired(courtFees);
  const docCount = documents.length;
  const hasPaymentMethod = !!orderDetails.paymentMethodId;

  // Filing review summary (Walter R42): what's about to go to the court —
  // court, case, documents, filed-by — in one look before the submit action.
  const countyLabel = orderInfo.county
    ? orderInfo.county.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : '—';
  const filingTypeLabel =
    orderInfo.filingType === 'subsequent-filing' ? 'Existing case' : 'New case';
  const leadDoc = documents.find(d => d.isLeadDocument);
  const leadDocLabel = leadDoc?.documentType?.name || leadDoc?.fileName;
  const filedByParty = parties.find(p => p.isLeadClient) ?? parties[0];
  const filedByName = filedByParty
    ? filedByParty.partyType === 'person'
      ? [filedByParty.firstName, filedByParty.lastName].filter(Boolean).join(' ')
      : filedByParty.organizationName || '—'
    : '—';
  const filedByAttorney = filedByParty
    ? attorneys.find(a => (a.representedPartyIds ?? []).includes(filedByParty.id))
    : undefined;
  const filedByDetail = filedByParty
    ? filedByParty.isSelfRepresented || !filedByAttorney
      ? 'Self-represented'
      : `Represented by ${[filedByAttorney.firstName, filedByAttorney.lastName].filter(Boolean).join(' ')}`
    : 'Add parties on About Your Case';

  const reviewSummary = [
    {
      label: 'Court',
      value: countyLabel,
      detail: [filingTypeLabel, caseInfo.caseCategoryName].filter(Boolean).join(' · '),
    },
    { label: 'Case', value: caseInfo.caseTitle || '—', detail: caseInfo.caseType?.name },
    {
      label: 'Documents',
      value: `${docCount} document${docCount !== 1 ? 's' : ''}`,
      detail: leadDocLabel ? `Lead: ${leadDocLabel}` : undefined,
    },
    { label: 'Filed by', value: filedByName, detail: filedByDetail },
  ];

  const checklistItems: ChecklistItemData[] = [
    {
      title: 'Documents are text-searchable',
      subtitle: 'Required so the court can index your filing',
      done: docCount > 0,
    },
    {
      title: 'Court verified',
      subtitle: `${wizardState.orderInfo.county ? wizardState.orderInfo.county.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Selected court'} accepts e-filings`,
      done: !!wizardState.orderInfo.county,
    },
    {
      title: 'All parties identified',
      subtitle: `${parties.length} part${parties.length !== 1 ? 'ies' : 'y'}, ${attorneys.length} attorney${attorneys.length !== 1 ? 's' : ''} on file`,
      done: parties.length >= 1,
    },
    {
      title: 'Payment method',
      // Walter R43: show what will be charged, with a way to change it.
      // SPEC-SOR-343 (verdict consumer VC3): the demand keys on the shared
      // decider over the resolved record — a resolved $0.00 total (a waived
      // filing lands there) needs no card, and an uncalculated quote asks for
      // nothing yet, while the validator's not-resolved rule keeps Submit
      // blocked and this row stops claiming otherwise.
      subtitle:
        feeTotal === null
          ? FEE_TOTAL_UNRESOLVED_LABEL
          : paymentRequired
            ? hasPaymentMethod
              ? `Card on file — charged ${feeTotal.text} on submit`
              : 'Add a card to complete your filing'
            : `No card needed — the total for this filing is ${feeTotal.text}`,
      done: feeTotal !== null && (!paymentRequired || hasPaymentMethod),
      action:
        feeTotal !== null && (paymentRequired || hasPaymentMethod) ? (
          hasPaymentMethod ? (
            <button
              className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:opacity-80"
              style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
              onClick={() => updateOrderDetails({ paymentMethodId: '' })}
            >
              Change
            </button>
          ) : (
            <button
              className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:opacity-80"
              style={{ borderColor: brand.colors.primary, color: brand.colors.link }}
              onClick={() => updateOrderDetails({ paymentMethodId: 'placeholder' })}
            >
              Add card
            </button>
          )
        ) : undefined,
    },
  ];

  return (
    <div className="max-w-2xl">
      {/* Section header */}
      <div className="mb-6">
        <h2
          className="text-xl font-bold mb-1"
          style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
        >
          You're almost done
        </h2>
        <p className="text-sm" style={{ color: brand.colors.textLight }}>
          One last look before we transmit to the court.
        </p>
      </div>

      {/* Filing review summary (Walter R42) — court, case, documents,
          filed-by, in one glance before the submit action. */}
      <div
        data-testid="filing-review-summary"
        className="rounded-xl mb-6 grid grid-cols-2 sm:grid-cols-4 gap-px overflow-hidden"
        style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.border }}
      >
        {reviewSummary.map(item => (
          <div key={item.label} className="p-4" style={{ backgroundColor: '#FFFFFF' }}>
            <div
              className="text-[11px] font-semibold uppercase tracking-wide mb-1"
              style={{ color: brand.colors.textLight }}
            >
              {item.label}
            </div>
            <div className="text-sm font-semibold" style={{ color: brand.colors.text }}>
              {item.value}
            </div>
            {item.detail && (
              <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
                {item.detail}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pre-submit checklist */}
      <div
        className="rounded-xl overflow-hidden mb-6"
        style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: '#FFFFFF' }}
      >
        {checklistItems.map((item, idx) => (
          <ChecklistItem
            key={item.title}
            item={item}
            brand={brand}
            isLast={idx === checklistItems.length - 1}
          />
        ))}
      </div>

      {/* Optional fields */}
      <div className="mb-6">
        <label
          htmlFor="order-message-to-clerk"
          className="block text-sm font-medium mb-1.5"
          style={{ color: brand.colors.text }}
        >
          Add a message to the court clerk{' '}
          <span className="font-normal" style={{ color: brand.colors.textLight }}>(optional)</span>
        </label>
        <textarea
          id="order-message-to-clerk"
          value={orderDetails.messageToClerk}
          onChange={e => updateOrderDetails({ messageToClerk: e.target.value })}
          placeholder="e.g., Please process before end of business day."
          rows={3}
          className="w-full px-4 py-3 rounded-lg text-sm resize-none transition-colors"
          style={{
            border: `1px solid ${brand.colors.border}`,
            color: brand.colors.text,
            outline: 'none',
          }}
          onFocus={e => (e.target.style.borderColor = brand.colors.primary)}
          onBlur={e => (e.target.style.borderColor = brand.colors.border)}
        />
      </div>

      <div className="mb-6">
        <label
          htmlFor="order-reference-number"
          className="block text-sm font-medium mb-1.5"
          style={{ color: brand.colors.text }}
        >
          Reference number{' '}
          <span className="font-normal" style={{ color: brand.colors.textLight }}>(for your records, optional)</span>
        </label>
        <input
          id="order-reference-number"
          type="text"
          value={orderDetails.internalReferenceNumber}
          onChange={e => updateOrderDetails({ internalReferenceNumber: e.target.value })}
          placeholder="e.g., Matter #ABC-123"
          className="w-full px-4 py-3 rounded-lg text-sm transition-colors"
          style={{
            border: `1px solid ${brand.colors.border}`,
            color: brand.colors.text,
            outline: 'none',
          }}
          onFocus={e => (e.target.style.borderColor = brand.colors.primary)}
          onBlur={e => (e.target.style.borderColor = brand.colors.border)}
        />
      </div>

      {/* After you submit timeline */}
      <div
        className="rounded-xl p-5 mb-6"
        style={{ backgroundColor: brand.colors.bgLight }}
      >
        <h3
          className="text-sm font-bold mb-3"
          style={{ color: brand.colors.secondary }}
        >
          After you submit
        </h3>
        <ol className="flex flex-col gap-3">
          <TimelineStep
            num={1}
            title="We send it to the court"
            subtitle="Usually within minutes — instantly during business hours"
            brand={brand}
          />
          <TimelineStep
            num={2}
            title="The court reviews"
            subtitle="Most filings are accepted in 24–48 business hours"
            brand={brand}
          />
          {/* The sequence ends in a decision, not an assumed acceptance
              (Walter R44: Submitted → Court review → Accepted / Rejected). */}
          <TimelineStep
            num={3}
            title="Accepted or rejected"
            subtitle="We email you the outcome — with the court-stamped copy when accepted"
            brand={brand}
          />
        </ol>
      </div>

      {/* Legal disclaimer */}
      <div
        className="rounded-xl p-4 mb-6"
        style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: '#FFFFFF' }}
      >
        <p className="text-xs font-semibold mb-1" style={{ color: brand.colors.text }}>
          By submitting, you agree:
        </p>
        <p className="text-xs leading-relaxed" style={{ color: brand.colors.textLight }}>
          Pursuant to CRC 2.251(b)(1)(C), you consent to electronic service of documents
          in this action. You also authorize us to charge your selected payment method for
          the total amount of fees.
        </p>
      </div>

      {/* Submit stack (metadata + button) */}
      <div className="flex flex-col items-end">
        <p className="text-xs mb-1.5" style={{ color: brand.colors.textLight }}>
          {`${feeTotal ? feeTotal.text : FEE_TOTAL_UNRESOLVED_LABEL} • ${docCount} document${docCount !== 1 ? 's' : ''} ready`}
        </p>
      </div>
    </div>
  );
};

interface ChecklistItemData {
  title: string;
  subtitle: string;
  done: boolean;
  action?: React.ReactNode;
}

interface ChecklistItemProps {
  item: ChecklistItemData;
  brand: BrandConfig;
  isLast: boolean;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({ item, brand, isLast }) => (
  <div
    className="flex items-center gap-3.5 px-5 py-3.5"
    style={{ borderBottom: isLast ? 'none' : `1px solid ${brand.colors.border}` }}
  >
    {/* Check circle */}
    {item.done ? (
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: '#0F9D6B' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    ) : (
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ border: `2px solid ${brand.colors.border}`, backgroundColor: '#FFFFFF' }}
      >
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: brand.colors.border }} />
      </div>
    )}

    {/* Text */}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold" style={{ color: brand.colors.text }}>
        {item.title}
      </div>
      <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
        {item.subtitle}
      </div>
    </div>

    {/* Action button */}
    {item.action}
  </div>
);

interface TimelineStepProps {
  num: number;
  title: string;
  subtitle: string;
  brand: BrandConfig;
}

const TimelineStep: React.FC<TimelineStepProps> = ({ num, title, subtitle, brand }) => (
  <li className="flex items-start gap-3">
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
      style={{
        backgroundColor: '#FFFFFF',
        border: `1.5px solid ${brand.colors.border}`,
        color: brand.colors.textLight,
      }}
    >
      {num}
    </div>
    <div>
      <div className="text-sm font-semibold" style={{ color: brand.colors.text }}>
        {title}
      </div>
      <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
        {subtitle}
      </div>
    </div>
  </li>
);

export default OrderDetailsTab;
